import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Shield, Activity, AlertTriangle, LogOut, RefreshCw, Wifi } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';

const fmtTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const SecurityDashboard = ({ user }) => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [rateAlerts, setRateAlerts] = useState({ alerts: [], warn_limit: 10, hard_limit: 20 });
  const [multiIp, setMultiIp] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, m] = await Promise.all([
        api.get('/security/active-sessions'),
        api.get('/security/rate-limit-alerts'),
        api.get('/security/multi-ip-alerts'),
      ]);
      setActiveSessions(s.data || []);
      setRateAlerts(r.data || { alerts: [], warn_limit: 10, hard_limit: 20 });
      setMultiIp(m.data || []);
    } catch {
      toast.error('Failed to load security data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, [loadAll]);

  const forceLogout = async (sess) => {
    if (!window.confirm(`Force logout ${sess.user_name}? They will be kicked out immediately and need to log in again.`)) return;
    try {
      await api.post(`/security/force-logout/${sess.user_id}`);
      toast.success(`${sess.user_name} has been logged out`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Force logout failed');
    }
  };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center text-gray-500">Admin only</div>;
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="security-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-rose-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Security Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Live sessions · Rate-limit alerts · Multi-IP detection</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="security-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Activity className="w-8 h-8 text-emerald-500" />
            <div>
              <p className="text-xs text-gray-500">Online Now</p>
              <p className="text-xl font-bold text-gray-800">{activeSessions.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-xs text-gray-500">Rate-Limit Flags (24h)</p>
              <p className="text-xl font-bold text-gray-800">{rateAlerts.alerts.filter(a => a.flag !== 'ok').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Wifi className="w-8 h-8 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Multi-IP Sessions</p>
              <p className="text-xl font-bold text-gray-800">{multiIp.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Shield className="w-8 h-8 text-indigo-500" />
            <div>
              <p className="text-xs text-gray-500">Limits / hour</p>
              <p className="text-xs font-mono text-gray-700">warn {rateAlerts.warn_limit} · block {rateAlerts.hard_limit}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Sessions */}
      <Card data-testid="active-sessions-card">
        <CardContent className="p-0">
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-blue-50 border-b border-emerald-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-600" />
            <span className="font-bold text-gray-800">Live Online Users</span>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{activeSessions.length} online</Badge>
            <span className="text-xs text-gray-500 ml-auto">Updated every 30s</span>
          </div>
          {activeSessions.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No active sessions</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-medium">User</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">Code</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">IP</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Last Seen</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">Action</th>
                </tr></thead>
                <tbody>
                  {activeSessions.map(s => (
                    <tr key={`${s.user_id}-${s.ip}`} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-xs font-medium text-gray-800">{s.user_name}</td>
                      <td className="px-3 py-2 text-center font-mono text-xs text-gray-500">{s.watermark_code || '—'}</td>
                      <td className="px-3 py-2 text-center"><Badge className="text-[9px] bg-gray-200 text-gray-600">{s.role}</Badge></td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600">{s.ip}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{fmtTime(s.last_seen)}</td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 h-7 px-2 text-xs"
                          onClick={() => forceLogout(s)} data-testid={`force-logout-${s.user_id}`}>
                          <LogOut className="w-3 h-3 mr-1" /> Kick
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rate-Limit Alerts */}
      <Card data-testid="rate-alerts-card">
        <CardContent className="p-0">
          <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="font-bold text-gray-800">Download Activity (24h)</span>
            <span className="text-xs text-gray-500 ml-auto">
              ⚠️ Warn @ {rateAlerts.warn_limit}/hr · 🚫 Block @ {rateAlerts.hard_limit}/hr
            </span>
          </div>
          {rateAlerts.alerts.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No download activity in the last 24h</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-medium">User</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">Code</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">24h</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">Last 1h</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Last DL</th>
                  <th className="px-3 py-2 text-center text-xs font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {rateAlerts.alerts.map(a => (
                    <tr key={a.user_id} className={`border-b border-gray-100 ${a.flag === 'blocked' ? 'bg-rose-50' : a.flag === 'warning' ? 'bg-amber-50' : ''}`}>
                      <td className="px-3 py-2 text-xs font-medium">{a.user_name}</td>
                      <td className="px-3 py-2 text-center font-mono text-xs text-gray-500">{a.watermark_code || '—'}</td>
                      <td className="px-3 py-2 text-center text-xs font-bold">{a.count_24h}</td>
                      <td className="px-3 py-2 text-center text-xs">
                        <span className={a.count_1h >= rateAlerts.hard_limit ? 'text-rose-700 font-bold' : a.count_1h >= rateAlerts.warn_limit ? 'text-amber-700 font-bold' : ''}>{a.count_1h}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{fmtTime(a.last_download)}</td>
                      <td className="px-3 py-2 text-center">
                        {a.flag === 'blocked' ? <Badge className="bg-rose-100 text-rose-700 text-[10px]">🚩 BLOCKED</Badge>
                          : a.flag === 'warning' ? <Badge className="bg-amber-100 text-amber-800 text-[10px]">⚠️ Warning</Badge>
                          : <Badge className="bg-gray-100 text-gray-600 text-[10px]">OK</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Multi-IP Alerts */}
      <Card data-testid="multi-ip-card">
        <CardContent className="p-0">
          <div className="px-4 py-3 bg-gradient-to-r from-rose-50 to-pink-50 border-b border-rose-100 flex items-center gap-2">
            <Wifi className="w-4 h-4 text-rose-600" />
            <span className="font-bold text-gray-800">Multi-IP Login Detection (30 min)</span>
            <span className="text-xs text-gray-500 ml-auto">Same user logged in from 2+ IPs simultaneously</span>
          </div>
          {multiIp.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No suspicious multi-IP sessions</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-medium">User</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">IPs</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Last Seen</th>
                </tr></thead>
                <tbody>
                  {multiIp.map(m => (
                    <tr key={m.user_id} className="border-b border-gray-100 bg-rose-50/30">
                      <td className="px-3 py-2 text-xs font-medium text-rose-800">{m.user_name}</td>
                      <td className="px-3 py-2 text-xs font-mono">
                        {m.ips.map(ip => <Badge key={ip} className="bg-rose-100 text-rose-700 text-[10px] mr-1">{ip}</Badge>)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{fmtTime(m.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityDashboard;
